import { clsx } from "clsx";
import { useRef } from "react";
import { mergeProps } from "react-aria";

import { useMeasureOnResize } from "../../hooks/useMeasureOnResize.ts";
import { useTooltip } from "../../hooks/useTooltip.ts";
import { createTextWidthMeasurer } from "../../utils/string/createTextMeasurer.ts";
import { WIDTH_BUFFER } from "../../utils/string/truncate-list.ts";
import { AnimatedTooltip } from "../Tooltip/AnimatedTooltip.tsx";
import css from "./Truncate.module.css";

interface FitTextProps {
  candidates: readonly string[];
  tooltipText?: string;
  renderText?: (text: string) => React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type FitTextDomProps = Omit<React.HTMLAttributes<HTMLSpanElement>, "children">;

export function FitText({
  candidates,
  tooltipText,
  renderText,
  className = "",
  style,
  ...props
}: FitTextProps & FitTextDomProps) {
  const fallback = candidates.at(-1) ?? "";
  const fullText = tooltipText ?? candidates[0] ?? fallback;
  const elementRef = useRef<HTMLSpanElement>(null);

  const measureText = (element: HTMLSpanElement, shouldCancel: () => boolean) => {
    const containerWidth = element.clientWidth;
    if (containerWidth === 0) {
      return fallback;
    }

    const measure = createTextWidthMeasurer(element);
    let next = fallback;

    for (const candidate of candidates) {
      if (shouldCancel()) {
        return fallback;
      }

      if (!candidate) continue;
      if (measure(candidate) - containerWidth <= WIDTH_BUFFER) {
        next = candidate;
        break;
      }
    }

    return next;
  };

  const displayText = useMeasureOnResize(elementRef, measureText, [candidates, fallback], fallback);
  const isDisabled = displayText === fullText;
  const { state, tooltipProps, triggerProps } = useTooltip(elementRef, { isDisabled });

  return (
    <>
      <span
        ref={elementRef}
        className={clsx(css.text, className)}
        style={style}
        data-testid="fit-text"
        {...mergeProps(props, triggerProps)}
        aria-label={fullText}
      >
        {renderText ? renderText(displayText) : displayText}
      </span>
      <AnimatedTooltip
        domProps={tooltipProps}
        state={state}
        targetRef={elementRef}
        title={fullText}
        nonInteractive
        offset={5}
        style={{ maxWidth: "unset" }}
      />
    </>
  );
}
